// Generic type for an event handler, taking a tuple of arguments
type EventHandler<Args extends unknown[]> = (...args: [...Args, { source: TEventSource }]) => void;

/**
 * Used when generating a new EventBus file. If you see this used, it is a placeholder.
 */
export type EmptyEventMap = Record<string, never>;

/**
 * Used to track the source of an event.
 *
 * @example
 * ```ts
 * addOnsEventBus.emit('addOnAdded', id, option, { source: 'onMount' });
 * ```
 */
export type TEventSource = 'user' | 'api' | 'onMount' | 'asyncResponse';

export interface EventConnection {
  on: () => void;
  off: () => void;
}

export class EventBus<Events extends Record<string, unknown[]>> {
  private events: Map<keyof Events, EventHandler<unknown[]>[]>;

  constructor() {
    this.events = new Map();
  }

  // Create a connection bound to a specific event and handler
  public bindTo<K extends keyof Events>(event: K, handler: EventHandler<Events[K]>): EventConnection {
    return {
      on: () => this.on(event, handler),
      off: () => this.off(event, handler),
    };
  }

  // Subscribe to an event with a handler that matches the event's argument types
  private on<K extends keyof Events>(event: K, handler: EventHandler<Events[K]>): void {
    if (!this.events.has(event)) {
      this.events.set(event, []);
    }

    this.events.get(event)?.push(handler as EventHandler<unknown[]>);
  }

  // Unsubscribe from an event
  private off<K extends keyof Events>(event: K, handler: EventHandler<Events[K]>): void {
    if (this.events.has(event)) {
      const handlers = this.events.get(event)?.filter((h) => h !== (handler as EventHandler<unknown[]>));

      if (handlers && handlers.length > 0) {
        this.events.set(event, handlers);
      } else {
        this.events.delete(event);
      }
    }
  }

  // Emit an event with the correct argument types
  public emit<K extends keyof Events>(event: K, ...args: [...Events[K], { source: TEventSource }]): void {
    if (this.events.has(event)) {
      this.events.get(event)?.forEach((handler) => handler(...args));
    }
  }

  // Clear all handlers for a specific event
  public clear<K extends keyof Events>(event: K): void {
    if (this.events.has(event)) {
      this.events.delete(event);
    }
  }
}
